import { useMemo, useState, useEffect } from "react";
import { useFilters, useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";

import { languages, strCode } from "../../../../../consts";
import { t } from "../../../../../services";
import { SearchInput, Table, WrapperContainer } from "../../../../../components";
import { LocalizationsTableColumns } from "./LocalizationsTableColumns";
import { appsUtil } from "../../../../../utils";

export const LocalizationsTable = ({
  active,
  marketLocalizations,
  onToggleValue,
  onSelectDefault
}) => {
  const [searchValue, setSearchValue] = useState("");

  const availableLanguages = appsUtil.getAvailableLanguages();

  useEffect(() => {
    if (!active) {
      handleFilterChange("");
    }
  }, [active]);

  const localizationData = useMemo(() => {
    if (availableLanguages) {
      return availableLanguages.map((language) => {
        const languageOption = languages.find((l) => l.id === language);
        const marketLocalization = marketLocalizations?.find((l) => l.name === language);

        const fullName = languageOption
          ? `${languageOption?.id} - ${languageOption?.value}`
          : language;

        return {
          name: language,
          fullName,
          isSelected: !!marketLocalization,
          isDefault: marketLocalization?.isDefault
        };
      });
    }

    return [];
  }, [marketLocalizations]);

  const columns = useMemo(() => LocalizationsTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data: localizationData,
      initialState: {
        sortBy: [
          {
            id: "fullName",
            asc: true
          }
        ]
      }
    },
    useFilters,
    useSortBy
  );

  const { setFilter } = tableInstance;

  const handleFilterChange = (value) => {
    setFilter("fullName", value);
    setSearchValue(value);
  };

  return (
    <WrapperContainer width={598}>
      <WrapperContainer width="100%" mb={16}>
        <SearchInput
          id="localizations-modal-search"
          value={searchValue}
          setValue={handleFilterChange}
          placeholder={t(strCode.SEARCH_MARKETS_PLACEHOLDER)}
        />
      </WrapperContainer>

      {localizationData && (
        <Table
          tableInstance={tableInstance}
          searchedText={searchValue}
          searchInfo={`${t(strCode.NO_MARKET_CODE_FOUND)} '${searchValue}'.`}
          searchCheckInfo={t(strCode.CHECK_MARKET_CODE_MESSAGE)}
          callbackFn1={onToggleValue}
          callbackFn2={onSelectDefault}
          maxTableHeight="186px"
          maxVisibleRows={1}
          showTableBorder
          modalTable
          modalTableSmall
        />
      )}
    </WrapperContainer>
  );
};

LocalizationsTable.propTypes = {
  active: PropTypes.bool.isRequired,
  marketLocalizations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      isDefault: PropTypes.bool.isRequired
    }).isRequired
  ).isRequired,
  onToggleValue: PropTypes.func.isRequired,
  onSelectDefault: PropTypes.func.isRequired
};
